/**
 * Track tile codec + collision-map baking.
 *
 * A Track is a 49x25 grid of Tiles. For physics we "bake" it into a
 * FIELD_WIDTH x FIELD_HEIGHT grid of surface codes (one per pixel) by applying
 * each tile's shape/special mask — exactly like the original engine, which gave
 * smooth diagonal and curved bank shots.
 */
import { Surface, Shape, type Tile, type Track, type Vec2 } from '../types.js';
import { TILE_SIZE, MAP_COLS, MAP_ROWS, FIELD_WIDTH, FIELD_HEIGHT } from '../constants.js';
import { shapeAt } from './shapes.js';
import { isSolid, isWater } from './surfaces.js';

// ─────────────────────────────────────────────────────────────
// Tile codec (matches the original 4-byte packing)
//   normal:  (1<<24) | (shape<<16) | (bg<<8) | fg
//   special: (2<<24) | ((special-24)<<16) | (bg<<8) | 0
// ─────────────────────────────────────────────────────────────
export function encodeTile(t: Tile): number {
  if (t.special === null) {
    return ((1 << 24) | (t.shape << 16) | (t.bg << 8) | t.fg) >>> 0;
  }
  const specialIndex = t.special - Surface.Start; // 0..27
  return ((2 << 24) | (specialIndex << 16) | (t.bg << 8) | 0) >>> 0;
}

export function decodeTile(code: number): Tile {
  const kind = (code >>> 24) & 0xff;
  const mid = (code >>> 16) & 0xff;
  const bg = (code >>> 8) & 0xff;
  const fg = code & 0xff;
  if (kind === 2) {
    return { special: (Surface.Start + mid) as Surface, shape: Shape.Blank, bg, fg: bg };
  }
  return { special: null, shape: mid as Shape, bg, fg };
}

export function makeTile(bg: Surface, fg: Surface, shape: Shape): Tile {
  return { special: null, shape, bg, fg };
}

export function makeSpecial(special: Surface, bg: Surface = Surface.Grass): Tile {
  return { special, shape: Shape.Blank, bg, fg: bg };
}

export const GRASS_TILE: Tile = { special: null, shape: Shape.Blank, bg: Surface.Grass, fg: Surface.Grass };

// ─────────────────────────────────────────────────────────────
// Per-pixel surface for a tile
// ─────────────────────────────────────────────────────────────
function circleAt(px: number, py: number, r: number): boolean {
  const c = (TILE_SIZE - 1) / 2;
  const dx = px - c;
  const dy = py - c;
  return dx * dx + dy * dy <= r * r;
}

function pixelSurface(t: Tile, px: number, py: number): Surface {
  if (t.special === null) {
    return shapeAt(t.shape, px, py) ? t.fg : t.bg;
  }
  const s = t.special;
  // Solids: full-tile hitbox
  if (isSolid(s)) return s;
  switch (s) {
    case Surface.Hole:
    case Surface.FakeHole:
      return circleAt(px, py, 6) ? s : t.bg;
    case Surface.Mine:
    case Surface.BigMine:
    case Surface.BlownMine:
    case Surface.BlownBigMine:
      return circleAt(px, py, s === Surface.BigMine ? 6.5 : 5) ? s : t.bg;
    case Surface.TeleBlueIn:
    case Surface.TeleBlueOut:
    case Surface.TeleRedIn:
    case Surface.TeleRedOut:
    case Surface.TeleYellowIn:
    case Surface.TeleYellowOut:
    case Surface.TeleGreenIn:
    case Surface.TeleGreenOut:
      return circleAt(px, py, 5) ? s : t.bg;
    case Surface.MagnetAttract:
    case Surface.MagnetRepel:
      // A small passable marker circle: the simulator scans these to build the
      // magnet force list, and the renderer paints them.
      if (isWater(t.bg)) return t.bg;
      return circleAt(px, py, 4) ? s : t.bg;
    case Surface.SunkMoveable:
      return s; // a block sunk into water becomes a walkable platform
    case Surface.Start:
    case Surface.StartBlue:
    case Surface.StartRed:
    case Surface.StartYellow:
    case Surface.StartGreen:
      return t.bg; // passable
    default:
      return t.bg;
  }
}

/** Baked collision map: one Surface code per native pixel (row-major). */
export interface CollisionMap {
  width: number;
  height: number;
  cells: Uint8Array;
}

export function buildCollisionMap(track: Track): CollisionMap {
  const cells = new Uint8Array(FIELD_WIDTH * FIELD_HEIGHT);
  for (let ty = 0; ty < MAP_ROWS; ty++) {
    for (let tx = 0; tx < MAP_COLS; tx++) {
      const tile = track.tiles[ty * MAP_COLS + tx] ?? GRASS_TILE;
      for (let py = 0; py < TILE_SIZE; py++) {
        const gy = ty * TILE_SIZE + py;
        const rowBase = gy * FIELD_WIDTH + tx * TILE_SIZE;
        for (let px = 0; px < TILE_SIZE; px++) {
          cells[rowBase + px] = pixelSurface(tile, px, py);
        }
      }
    }
  }
  return { width: FIELD_WIDTH, height: FIELD_HEIGHT, cells };
}

/** Sample the collision map, clamping to the field (outer walls are implicit). */
export function surfaceAt(map: CollisionMap, x: number, y: number): Surface {
  let ix = x | 0;
  let iy = y | 0;
  if (ix < 0) ix = 0;
  else if (ix >= map.width) ix = map.width - 1;
  if (iy < 0) iy = 0;
  else if (iy >= map.height) iy = map.height - 1;
  return map.cells[iy * map.width + ix] as Surface;
}

// ─────────────────────────────────────────────────────────────
// Track helpers
// ─────────────────────────────────────────────────────────────
export function tileCenter(col: number, row: number): Vec2 {
  return { x: col * TILE_SIZE + TILE_SIZE / 2, y: row * TILE_SIZE + TILE_SIZE / 2 };
}

export function findFirst(track: Track, predicate: (s: Surface | null) => boolean): Vec2 | null {
  for (let i = 0; i < track.tiles.length; i++) {
    const t = track.tiles[i];
    if (predicate(t.special)) {
      const col = i % MAP_COLS;
      const row = Math.floor(i / MAP_COLS);
      return tileCenter(col, row);
    }
  }
  return null;
}

export function resolveTee(track: Track): Vec2 {
  if (track.tee) return track.tee;
  const start = findFirst(track, (s) => s === Surface.Start || (s !== null && s >= Surface.StartBlue));
  return start ?? { x: FIELD_WIDTH / 2, y: FIELD_HEIGHT / 2 };
}

export function resolveCup(track: Track): Vec2 {
  if (track.cup) return track.cup;
  const hole = findFirst(track, (s) => s === Surface.Hole);
  return hole ?? { x: FIELD_WIDTH / 2, y: TILE_SIZE * 2 };
}

/** Encode all tiles to numbers for network transport. */
export function encodeTrackTiles(track: Track): number[] {
  return track.tiles.map(encodeTile);
}

export function decodeTrackTiles(codes: number[]): Tile[] {
  return codes.map(decodeTile);
}

/**
 * Apply persistent stroke mutations (blown mines, broken walls, moved blocks)
 * to a track's tiles in place. Server and clients run this identically, so a
 * rebuilt collision map / repaint stays in lockstep with the simulation.
 */
export function applyMutationsToTrack(track: Track, mutations: Array<{ col: number; row: number; to: Surface | null }>): void {
  for (const m of mutations) {
    if (m.col < 0 || m.col >= MAP_COLS || m.row < 0 || m.row >= MAP_ROWS) continue;
    const t = track.tiles[m.row * MAP_COLS + m.col];
    if (!t) continue;
    if (m.to === null) {
      t.special = null;
      t.shape = Shape.Blank;
      t.fg = t.bg;
    } else {
      t.special = m.to;
      t.shape = Shape.Blank;
      t.fg = t.bg;
    }
  }
}
